import { WebSocketClient } from '@/modules/WebSocketClient';
import { CustomEvents } from './WebSocketClient';
import { v4 as uuid } from 'uuid';
import { Events, EventSystem, UNIVERSAL_TYPE } from './EventSystem';
import { Message } from '@/store/entities/Message';
import { userModule } from '@/store';

enum MessageType {
  GUESS_WORDS_MESSAGE = 1,
  ANSWER_WORDS_MESSAGE = 2,
  SUPER_TURN_MESSAGE = 3,
  CONTINUE_MESSAGE = 4,
  RESTART_MESSAGE = 5,
  UPGRADE_MESSAGE = 6,
  CLIENT_MESSAGE = 7,
  ROOM_INFO_MESSAGE = 8,
  FULL_INFO_MESSAGE = 9,
  DATA_INFO_MESSAGE = 10,
  VERIFY_DATA_MESSGE = 85,
  AWAKE_MESSAGE = 86
}

const RESUME_STORAGE_KEY = 'oneword_socket_lineage_v1'

class SocketClient<E extends Events> extends EventSystem<E> {
  reconnectInterval: any
  connectionStatusTimer: any
  connectionAttemptTimer: any
  status = false

  id = ''
  clientId = uuid()
  resumeToken = ''
  private bindingEpoch = 0
  private boundSocketId = ''
  socket: WebSocketClient<E> | null = null

  connected = false
  dead = false
  connecting = false

  hasConnection = true
  private connectionListeners: Set<(connected: boolean) => void> = new Set()
  connectionLostAt: number | null = null
  connectionAttemptStartedAt = 0

  private readonly connectionGracePeriod = 10000
  private readonly connectionAttemptTimeout = 10000

  private readonly handleOnline = () => {
    this.updateSocket()
  }

  private readonly handleOffline = () => {
    this.connectionLostAt = Date.now()
    this.setHasConnection(false)
  }

  constructor() {
    super()
    try {
      const stored = JSON.parse(window.localStorage.getItem(RESUME_STORAGE_KEY) || '{}')
      if (typeof stored.resumeToken === 'string') this.resumeToken = stored.resumeToken
      if (Number.isInteger(stored.bindingEpoch) && stored.bindingEpoch >= 0) {
        this.bindingEpoch = stored.bindingEpoch
      }
    }
    catch (_) {}
    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOffline)
  }

  clearReconnectInterval() {
    clearTimeout(this.reconnectInterval)
  }

  onConnectionChange(listener: (connected: boolean) => void) {
    this.connectionListeners.add(listener)
    listener(this.hasConnection)
    return () => this.connectionListeners.delete(listener)
  }

  private setHasConnection(connected: boolean) {
    if (this.hasConnection === connected) return
    this.hasConnection = connected
    this.connectionListeners.forEach(listener => listener(connected))
  }

  updateId(id: string) {
    if (this.id !== id) this.boundSocketId = ''
    this.id = id
    if (this.socket) {
      this.socket.id = id
    }
  }

  updateResumeToken(resumeToken?: string, bindingEpoch?: number) {
    if (resumeToken) this.resumeToken = resumeToken
    if (Number.isInteger(bindingEpoch) && (bindingEpoch || 0) > this.bindingEpoch) {
      this.bindingEpoch = bindingEpoch || 0
    }
    try {
      window.localStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify({
        resumeToken: this.resumeToken,
        bindingEpoch: this.bindingEpoch
      }))
    }
    catch (_) {}
  }

  nextBindingEpoch() {
    this.bindingEpoch++
    this.updateResumeToken()
    return this.bindingEpoch
  }

  hasCurrentSocketId() {
    const socket = this.socket
    const transport = socket?.getSocket()
    return !!this.id && socket?.id === this.id && transport?.readyState === WebSocket.OPEN
  }

  async waitForCurrentSocketId(timeoutMs = this.connectionAttemptTimeout) {
    const startedAt = Date.now()

    while (!this.hasCurrentSocketId()) {
      if (Date.now() - startedAt >= timeoutMs) return false
      await new Promise(resolve => setTimeout(resolve, 50))
    }

    return true
  }

  registerUpgradeComplete(socketId: string) {
    if (socketId === this.id) this.boundSocketId = socketId
  }

  async waitForCurrentRoomBinding(timeoutMs = this.connectionAttemptTimeout) {
    const startedAt = Date.now()

    while (!this.hasCurrentSocketId() || this.boundSocketId !== this.id) {
      if (Date.now() - startedAt >= timeoutMs) return false
      await new Promise(resolve => setTimeout(resolve, 50))
    }

    return true
  }

  cancelPendingIntent(actionId: string) {
    this.socket?.cancelPendingIntent(actionId)
  }

  scheduleReconnect(delay = 0) {
    this.markConnectionInterrupted()
    clearTimeout(this.reconnectInterval)
    this.reconnectInterval = setTimeout(() => {
      if (!navigator.onLine) return
      const transport = this.socket?.getSocket()
      if (transport?.readyState === WebSocket.OPEN) return
      this.updateSocket()
    }, delay)
  }

  init() {
    const wsUrl = process.env.VUE_APP_API_URL ?
      `${process.env.VUE_APP_API_URL.replace('http','ws')}/ws`
      : `${window.location.protocol.replace('http','ws')}//api.${window.location.host}/ws`
    const resumeQuery = this.resumeToken
      ? `?resumeToken=${encodeURIComponent(this.resumeToken)}`
      : ''
    this.socket = new WebSocketClient<E>(`${wsUrl}${resumeQuery}`, '', this)
    this.status = true
    this.dead = false
    this.connecting = true
    this.connectionAttemptStartedAt = Date.now()
  }

  updateSocket() {
    if (!navigator.onLine) {
      this.setHasConnection(false)
      return
    }

    const pendingMessages = this.socket?.pendingMessages() || []
    this.killSocket()
    this.clear()
    this.init()
    this.socket?.restorePendingMessages(pendingMessages)
    this.start()
  }

  upgrade({ roomId, gameId, previousRoomId, previousGameId }: {
    roomId?: string,
    gameId?: string,
    previousRoomId?: string,
    previousGameId?: string
  }) {
    console.log('upgrade')
    console.log(roomId, gameId, this.id)

    this.emit('upgrade', new Message({
      meta: {
        id: this.id,
        roomId: roomId || '',
        gameId: gameId || '',
        previousRoomId,
        previousGameId,
        bindingEpoch: this.nextBindingEpoch(),
        user: {
          id: userModule.id,
        },
        type: 'add'
      },
      content: {}
    }), MessageType.UPGRADE_MESSAGE)
  }

  start() {
    this.socket?.init()
    clearTimeout(this.connectionAttemptTimer)
    this.connectionAttemptTimer = setTimeout(() => {
      const transport = this.socket?.getSocket()
      if (transport?.readyState === WebSocket.CONNECTING) transport.close()
    }, this.connectionAttemptTimeout)
    this.events.forEach((events, key) => {
      events.forEach(event => {
        this.socket?.on(key, event.callback)
      })
    })
    this.status = true
    this.dead = false
  }

  updateConnect(value: boolean) {
    this.connected = value
  }

  markConnected() {
    clearTimeout(this.connectionAttemptTimer)
    clearTimeout(this.connectionStatusTimer)
    this.connectionLostAt = null
    this.setHasConnection(true)
  }

  markConnectionInterrupted() {
    if (this.connectionLostAt === null) {
      this.connectionLostAt = Date.now()
      clearTimeout(this.connectionStatusTimer)
      this.connectionStatusTimer = setTimeout(() => {
        if (this.connectionLostAt !== null) this.setHasConnection(false)
      }, this.connectionGracePeriod)
    }

    if (Date.now() - this.connectionLostAt >= this.connectionGracePeriod) {
      this.setHasConnection(false)
    }
  }

  killSocket() {
    this.socket?.clear()
    this.dead = true
    this.status = false
    this.connecting = false
    this.socket = null
  }

  close() {
    this.dead = true
  }

  emitter<K extends keyof E>(key: K, data: Parameters<E[K]>[0], type: number = UNIVERSAL_TYPE) {
    if (this.socket) {
      this.socket.emit(key, data, type)
    }
  }

  clear() {
    this.clearReconnectInterval()
    clearTimeout(this.connectionAttemptTimer)
  }
}

const socketClient = new SocketClient<CustomEvents>()

export { socketClient, MessageType, SocketClient }
